import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onComplete: (firstName: string, lastName: string) => void;
  onSkip?: () => void;
}

export function OnboardingNameModal({ open, onComplete, onSkip }: Props) {
  const { user } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleContinue() {
    if (!user) return;
    const fn = firstName.trim();
    const ln = lastName.trim();
    if (!fn || !ln) {
      toast.error("Please enter your first and last name");
      return;
    }
    setSaving(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .upsert(
          { id: user.id, email: user.email, first_name: fn, last_name: ln },
          { onConflict: "id" },
        );
      if (error) throw error;
      onComplete(fn, ln);
    } catch (e: any) {
      toast.error(e?.message ?? "Could not save your name");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl tracking-tight">
            Welcome! What's your name?
          </DialogTitle>
          <DialogDescription>
            We'll use this to label your tapes automatically.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="onb-first">First name</Label>
            <Input
              id="onb-first"
              autoFocus
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Buffy"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="onb-last">Last name</Label>
            <Input
              id="onb-last"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Summers"
            />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          {onSkip && (
            <Button variant="ghost" onClick={onSkip} disabled={saving}>
              Skip for now
            </Button>
          )}
          <Button onClick={handleContinue} disabled={saving}>
            {saving ? <Loader2 className="size-4 animate-spin" /> : "Continue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
